import Icon from '../Utilites/Icon';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchAreas, getCachedAreas } from '../Core/FetchData';
import SelectionThumbnail from '../Utilites/SelectionThumbnail';
import SelectionList from '../Utilites/SelectionList';
import { isAreaUnlocked, isAreaFree } from '../Core/Auth';
import freeKeyIcon from '../assets/free-key.svg';
import { getCalculatorUrl } from '../Core/Config';
import '../Core/i18n';

const AreaSelection = ({ onChange, lang, group, auth }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { t: selectionT } = useTranslation('selection');

    const [areas, setAreas] = useState(() => getCachedAreas(lang, group) || []);
    const [loading, setLoading] = useState(() => getCachedAreas(lang, group) === null);
    const [revealedAreaId, setRevealedAreaId] = useState(null);
    const revealedCard = useRef(null);
    // Vyhledávání je na přání klientky dočasně vypnuté.
    // const [filter, setFilter] = useState('');

    const changeArea = (area) => {
        if (!isAreaUnlocked(area, auth, lang, group)) {
            if (revealedAreaId === area.id) {
                revealedCard.current?.querySelector('.area-premium-overlay')?.click();
            } else {
                setRevealedAreaId(area.id);
            }
            return;
        }

        onChange(area);
        navigate('/quiz/select-quiz');
    };

    useEffect(() => {
        setRevealedAreaId(null);
    }, [lang?.code, group?.id, auth]);

    useEffect(() => {
        if (revealedAreaId === null) return;
        const dismissOutside = (event) => {
            if (!revealedCard.current?.contains(event.target)) setRevealedAreaId(null);
        };
        const dismissOnEscape = (event) => {
            if (event.key === 'Escape') {
                revealedCard.current?.querySelector('button')?.focus();
                setRevealedAreaId(null);
            }
        };
        document.addEventListener('pointerdown', dismissOutside, true);
        document.addEventListener('focusin', dismissOutside);
        document.addEventListener('keydown', dismissOnEscape);
        return () => {
            document.removeEventListener('pointerdown', dismissOutside, true);
            document.removeEventListener('focusin', dismissOutside);
            document.removeEventListener('keydown', dismissOnEscape);
        };
    }, [revealedAreaId]);

    useEffect(() => {
        if (!lang?.code) {
            navigate('/quiz');
        }
    }, [lang, navigate]);

    useEffect(() => {
        if (!group?.id) {
            navigate('/quiz/select-group');
        }
    }, [group, navigate]);

    useEffect(() => {
        let active = true;
        const cached = getCachedAreas(lang, group);
        setLoading(cached === null);
        setAreas(cached || []);
        const loadAreas = async () => {
            const fetchedAreas = await fetchAreas(lang, group);
            if (active) {
                setAreas(fetchedAreas);
                setLoading(false);
            }
        };
        loadAreas();
        return () => { active = false; };
    }, [lang, group]);

    const filteredAreas = areas;
    // const filteredAreas = areas.filter((area) =>
    //     area.name.toLowerCase().includes(filter.toLowerCase())
    // );

    // const clearFilter = () => {
    //     setFilter('');
    // };

    return (
        <div id="quiz-areaSelection">
            {/* Vyhledávání je dočasně vypnuté.
            <div id="filter-area">
                <div>
                    <input
                        id="filter-area-input"
                        type="text"
                        placeholder={t('filter-area')}
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="pr-8"
                        autoComplete="off"
                    />
                    {filter && (
                        <button
                            onClick={clearFilter}
                            id="clear-filter-area"
                            className=""
                            aria-label="Clear filter"
                        >
                            <Icon name="close" />
                        </button>
                    )}
                </div>
            </div>
            */}

            <SelectionList items={areas} loading={loading} loadingLabel={selectionT('loading')}>
                {filteredAreas.length > 0 ? (
                    filteredAreas.map((area) => {
                        const unlocked = isAreaUnlocked(area, auth, lang, group);
                        const revealed = !unlocked && revealedAreaId === area.id;

                        return (
                            <li key={`area-${area.id}`} ref={revealed ? revealedCard : null}
                                data-search={area.name.toLowerCase()}
                                className={`area ${unlocked ? 'area-unlocked' : 'area-locked'}${revealed ? ' area-premium-open' : ''}`}>
                                <button
                                    onClick={() => changeArea(area)}
                                    onFocus={(event) => {
                                        if (!unlocked && event.currentTarget.matches(':focus-visible')) {
                                            setRevealedAreaId(area.id);
                                        }
                                    }}
                                    type="button"
                                    title={unlocked ? area.name : selectionT('premium-quiz-area')}
                                    className={`btn-primary ${!unlocked ? 'area-locked' : ''}`}
                                    style={{ transitionDuration: '0.4s' }}
                                >
                                    {area.image && (
                                        <div>
                                            <SelectionThumbnail image={area.image} alt={area.name} />
                                        </div>
                                    )}

                                    <strong className="text-shadow">
                                        <span className="area-title-text">{area.name}</span>
                                    </strong>
                                    <span className="hidden text-[0.85rem] leading-[1.3rem] pt-1 opacity-100">
                                        {area.wordsCount} {t('words')}
                                    </span>
                                    {isAreaFree(area, auth, lang, group) && (
                                        <span className="area-free-badge">
                                            <span className="area-free-key" aria-hidden="true">
                                                <img src={freeKeyIcon} alt="" />
                                            </span>
                                            <span className="area-free-label">{selectionT('free-quiz-area')}</span>
                                        </span>
                                    )}
                                    {!unlocked && (
                                        <span className="area-lock-badge" aria-hidden="true">
                                            <Icon name="lock" />
                                        </span>
                                    )}
                                </button>
                                {!unlocked && (
                                    <a className="area-premium-overlay" href={getCalculatorUrl(lang.code)}
                                        aria-label={`${selectionT('buy-access')}: ${area.name}`}>
                                        <Icon name="lock" />
                                        <span className="area-premium-label">{selectionT('premium-quiz-area')}</span>
                                        <span className="area-buy-access">
                                            {selectionT('buy-access')}
                                        </span>
                                    </a>
                                )}
                            </li>
                        );
                    })
                ) : (
                    <li role="status">{loading ? selectionT('loading') : t('no-areas-found')}</li>
                )}
            </SelectionList>
        </div>
    );
};

export default AreaSelection;
